import {defineMessages, FormattedMessage, intlShape, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import Modal from '../../containers/modal.jsx';
// Reuse the exact same styling as the "Load Custom Extension" modal so the two
// dialogs look identical: same type selector, same inputs, same buttons.
import styles from '../tw-custom-extension-modal/custom-extension-modal.css';

// Single modal for adding an extension library. Inside it the user picks the
// source, exactly like the "Load Custom Extension" modal: either a remote
// manifest URL or a local folder of .js files. One button opens this modal;
// the choice between URL and local upload happens within it.
const messages = defineMessages({
    title: {
        defaultMessage: 'Add Extension Library',
        description: 'Title of the add extension library dialog',
        id: 'tw.extensionLibraryModal.title'
    },
    urlTab: {
        defaultMessage: 'URL',
        description: 'Tab to add a library from a remote manifest URL',
        id: 'tw.extensionLibraryModal.urlTab'
    },
    folderTab: {
        defaultMessage: 'Folder',
        description: 'Tab to add a library from a local folder',
        id: 'tw.extensionLibraryModal.folderTab'
    },
    urlLabel: {
        defaultMessage: 'Enter the extension library URL (a manifest, e.g. https://openwarp-extensions.pages.dev/):',
        description: 'Label for the extension library URL input',
        id: 'tw.extensionLibraryModal.urlLabel'
    },
    urlPlaceholder: {
        defaultMessage: 'https://openwarp-extensions.pages.dev/',
        description: 'Placeholder for the extension library URL input',
        id: 'tw.extensionLibraryModal.urlPlaceholder'
    },
    folderLabel: {
        defaultMessage: 'Select a local folder containing .js extension files. Metadata is read from each file\'s leading // comment header.',
        description: 'Label for the local folder picker',
        id: 'tw.extensionLibraryModal.folderLabel'
    },
    folderButton: {
        defaultMessage: 'Choose local folder',
        description: 'Button that opens the local folder picker',
        id: 'tw.extensionLibraryModal.folderButton'
    },
    folderNameLabel: {
        defaultMessage: 'Library name:',
        description: 'Label for the extension library name input',
        id: 'tw.extensionLibraryModal.folderNameLabel'
    },
    folderNamePlaceholder: {
        defaultMessage: 'Leave empty to use the folder name',
        description: 'Placeholder for the extension library name input',
        id: 'tw.extensionLibraryModal.folderNamePlaceholder'
    },
    load: {
        defaultMessage: 'Load',
        description: 'Button that loads the given extension library',
        id: 'tw.extensionLibraryModal.load'
    },
    invalid: {
        defaultMessage: 'Could not load that library. Make sure it is a valid extension library manifest URL.',
        description: 'Error shown when the library manifest could not be loaded',
        id: 'tw.extensionLibraryModal.invalid'
    }
});

const AddExtensionLibraryModal = props => {
    const [type, setType] = React.useState('url');
    const localInputRef = React.useRef(null);

    const handleLocalClick = () => {
        // On the desktop build the main process provides a native folder
        // picker that returns an array of parsed files.
        if (window.EditorPreload && window.EditorPreload.selectExtensionFolder) {
            window.EditorPreload.selectExtensionFolder()
                .then(result => {
                    if (result && Array.isArray(result.files)) {
                        // Pass the whole {root, files} so the caller can persist the
                        // folder path and re-read it after remounting.
                        props.onSelectLocal(result);
                    }
                })
                .catch(err => console.error(err));
        } else if (localInputRef.current) {
            // Fallback for the browser build: use a webkitdirectory input.
            localInputRef.current.value = '';
            localInputRef.current.click();
        }
    };

    return (
        <Modal
            className={styles.modalContent}
            onRequestClose={props.onClose}
            contentLabel={props.intl.formatMessage(messages.title)}
            id="addExtensionLibraryModal"
        >
            <div className={styles.body}>
                <div className={styles.typeSelectorContainer}>
                    <div
                        className={styles.typeSelectorButton}
                        data-active={type === 'url'}
                        onClick={() => setType('url')}
                        tabIndex={0}
                    >
                        <FormattedMessage {...messages.urlTab} />
                    </div>
                    <div
                        className={styles.typeSelectorButton}
                        data-active={type === 'folder'}
                        onClick={() => setType('folder')}
                        tabIndex={0}
                    >
                        <FormattedMessage {...messages.folderTab} />
                    </div>
                </div>

                {type === 'url' ? (
                    <React.Fragment key={type}>
                        <p>
                            <FormattedMessage {...messages.urlLabel} />
                        </p>
                        <input
                            type="text"
                            className={styles.urlInput}
                            value={props.url}
                            onChange={props.onChangeURL}
                            onKeyDown={props.onKeyDown}
                            placeholder={props.intl.formatMessage(messages.urlPlaceholder)}
                            autoFocus
                        />
                        {props.error ? (
                            <p className={styles.warningText}>
                                <FormattedMessage {...messages.invalid} />
                            </p>
                        ) : null}
                    </React.Fragment>
                ) : (
                    <React.Fragment key={type}>
                        <p>
                            <FormattedMessage {...messages.folderLabel} />
                        </p>
                        <label
                            htmlFor="addExtensionLibraryLocalName"
                            style={{display: 'block', margin: '1rem 0 0.25rem'}}
                        >
                            <FormattedMessage {...messages.folderNameLabel} />
                        </label>
                        <input
                            id="addExtensionLibraryLocalName"
                            type="text"
                            className={styles.urlInput}
                            value={props.localName}
                            onChange={props.onChangeLocalName}
                            placeholder={props.intl.formatMessage(messages.folderNamePlaceholder)}
                        />
                        <input
                            type="file"
                            webkitdirectory=""
                            directory=""
                            multiple
                            ref={localInputRef}
                            style={{display: 'none'}}
                            onChange={props.onSelectLocal}
                        />
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.loadButton}
                                onClick={handleLocalClick}
                            >
                                <FormattedMessage {...messages.folderButton} />
                            </button>
                        </div>
                    </React.Fragment>
                )}

                <div className={styles.buttonRow}>
                    {type === 'url' ? (
                        <button
                            className={styles.loadButton}
                            onClick={props.onLoad}
                            disabled={!props.url.trim()}
                        >
                            <FormattedMessage {...messages.load} />
                        </button>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

AddExtensionLibraryModal.propTypes = {
    intl: intlShape,
    url: PropTypes.string.isRequired,
    onChangeURL: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    onLoad: PropTypes.func.isRequired,
    onSelectLocal: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    localName: PropTypes.string,
    onChangeLocalName: PropTypes.func,
    error: PropTypes.bool
};

export default injectIntl(AddExtensionLibraryModal);
